/**
 * CategoryTaboola component
 * 
 * Renders Taboola widgets for Category Pages according to client specifications:
 * 1. Head Loader script (category page type) - EXACTLY ONCE per URL
 * 2. Right Rail Thumbnails widget (sidebar) - EXACTLY ONCE per URL
 * 3. Below Category Thumbnails widget (bottom) - EXACTLY ONCE per URL
 * 4. Flush execution - EXACTLY ONCE per URL
 */
import React, { useEffect, useRef } from "react";

const PUBLISHER_ID = "sandeshdigital-sandesh";
const PAGE_TYPE = "category";
const LOADER_URL = "//cdn.taboola.com/libtrc/" + PUBLISHER_ID + "/loader.js";
const LOADER_PRIVACY_URL = "//static.tblcontent.com/libtrc/" + PUBLISHER_ID + "/loader.privacy.js";
const PIXEL_URL = "https://static.qovani.com/libtrc/tr5?type=pixel&publisher=" + PUBLISHER_ID;
const SCRIPT_ID = "tb_loader_script";

let activeHeadUrl = null;
const initializedWidgets = new Set();
let flushTimeout = null;

// Ensure Head and Loader script are pushed and injected exactly ONCE per Category URL
const ensureCategoryHead = (url) => {
  if (typeof window === "undefined") return;
  window._taboola = window._taboola || [];

  if (activeHeadUrl === url) return;
  activeHeadUrl = url;

  const isLoaderAlreadyLoaded = !!(document.getElementById(SCRIPT_ID) || window.TRC);
  if (isLoaderAlreadyLoaded) {
    window._taboola.push({ notify: "newPageLoad" });
  }

  const pageTypePush = {};
  pageTypePush[PAGE_TYPE] = "auto";
  pageTypePush["url"] = url;
  window._taboola.push(pageTypePush);

  try {
    new Image().src = PIXEL_URL;
  } catch (e) {}

  if (!document.getElementById(SCRIPT_ID)) {
    const firstScript = document.getElementsByTagName("script")[0];
    const s = document.createElement("script");
    s.async = true;
    s.src = LOADER_URL;
    s.id = SCRIPT_ID;
    if (LOADER_PRIVACY_URL) {
      s.onerror = function () {
        if (s.parentNode) s.parentNode.removeChild(s);
        if (!document.getElementById(SCRIPT_ID + "_fb")) {
          const fb = document.createElement("script");
          fb.async = true;
          fb.src = LOADER_PRIVACY_URL;
          fb.id = SCRIPT_ID + "_fb";
          if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(fb, firstScript);
          } else {
            document.head.appendChild(fb);
          }
        }
      };
    }
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(s, firstScript);
    } else {
      document.head.appendChild(s);
    }
  }

  if (window.performance && typeof window.performance.mark === "function") {
    window.performance.mark("tbl_ic");
  }
};

// Debounce flush so if both widgets mount on the same page, flush is called only ONCE
const scheduleFlush = () => {
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(() => {
    window._taboola = window._taboola || [];
    window._taboola.push({ flush: true });
    flushTimeout = null;
  }, 40);
};

/**
 * CategoryRightRailTaboola component
 * Renders the "Right Rail Thumbnails" Taboola widget in category page sidebar
 */
export const CategoryRightRailTaboola = React.memo(() => {
  useEffect(() => {
    const currentUrl = window.location.href;
    const widgetKey = "right-rail:" + currentUrl;

    if (initializedWidgets.has(widgetKey)) return;
    initializedWidgets.add(widgetKey);

    ensureCategoryHead(currentUrl);

    window._taboola = window._taboola || [];
    window._taboola.push({
      mode: "thumbnails-rr",
      container: "taboola-right-rail-thumbnails",
      placement: "Right Rail Thumbnails",
      target_type: "mix",
    });

    scheduleFlush();
  }, []);

  return (
    <div className="my-2 sidebar-taboola">
      <div id="taboola-right-rail-thumbnails"></div>
    </div>
  );
});

/**
 * CategoryTaboola component
 * Renders the "Below Category Thumbnails" Taboola widget at category page bottom
 */
const CategoryTaboola = () => {
  const taboolaRef = useRef(null);

  useEffect(() => {
    const currentUrl = window.location.href;
    const widgetKey = "below-cat:" + currentUrl;

    if (!initializedWidgets.has(widgetKey)) {
      initializedWidgets.add(widgetKey);

      ensureCategoryHead(currentUrl);

      window._taboola = window._taboola || [];
      window._taboola.push({
        mode: "alternating-thumbnails-a",
        container: "taboola-below-category-thumbnails",
        placement: "Below Category Thumbnails",
        target_type: "mix",
      });

      scheduleFlush();
    }

    // Observer for footer bottom styling
    const element = taboolaRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const footerBottomElement = document.getElementById("footer_bottom");
          if (entry.isIntersecting) {
            footerBottomElement?.classList.add("fixed");
          } else {
            footerBottomElement?.classList.remove("fixed");
          }
        });
      },
      { root: null, rootMargin: "0px", threshold: 0 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="w-100 my-4 taboola" ref={taboolaRef}>
      <div id="taboola-below-category-thumbnails"></div>
    </div>
  );
};

export default React.memo(CategoryTaboola);
